import React, { FC, ReactNode, useState } from 'react';
import { Menu } from '@chakra-ui/react';
import { Group, Item, OptionGroupConfig } from './types';
import { MenuItem } from './menu-item';

const itemKey = (item: Item, index: number) =>
  `${typeof item.text === 'string' ? item.text : 'item'}-${item.value}-${index}`;

/** Holds one option group's selection, controlled or not, and reports changes through the caller's
 * `onChange`: a string for radio groups, an array for checkbox groups. */
export const useOptionGroupValue = (config: OptionGroupConfig | undefined, fallbackType: 'radio' | 'checkbox') => {
  const type = config?.menuGroupOptionType ?? fallbackType;
  const [uncontrolledValue, setUncontrolledValue] = useState<string | string[]>(
    config?.defaultValue ?? (type === 'radio' ? '' : [])
  );
  const value = config?.value ?? uncontrolledValue;
  const commit = (next: string | string[]) => {
    if (config?.value === undefined) setUncontrolledValue(next);
    config?.onChange?.(next);
  };
  const selectionCount = Array.isArray(value) ? value.length : value ? 1 : 0;
  return { type, value, commit, selectionCount };
};

type OptionGroupProps = {
  items: Item[];
  label?: ReactNode;
  group: ReturnType<typeof useOptionGroupValue>;
};

/** Radio selection lives in a RadioItemGroup that feeds its items by context, while checkbox items own their
 * checked state; both render from the one `OptionGroupConfig` the wrapper's callers pass. */
export const OptionGroup: FC<OptionGroupProps> = ({ items, label, group }) => {
  const { type, value, commit } = group;
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  const renderedItems = items.map((item, index) => (
    <MenuItem
      key={itemKey(item, index)}
      index={index}
      item={item}
      isMenuItemOptions
      type={type}
      value={item.value}
      isChecked={item.value !== undefined && selected.includes(item.value)}
      onCheckedChange={checked => {
        if (item.value === undefined) return;
        commit(checked ? [...selected, item.value] : selected.filter(entry => entry !== item.value));
      }}
    />
  ));

  if (type === 'radio') {
    return (
      <Menu.RadioItemGroup
        value={Array.isArray(value) ? (value[0] ?? '') : value}
        onValueChange={details => commit(details.value)}
      >
        {label}
        {renderedItems}
      </Menu.RadioItemGroup>
    );
  }

  return (
    <Menu.ItemGroup>
      {label}
      {renderedItems}
    </Menu.ItemGroup>
  );
};

// Owns one group's selection so the wrapper can render a list of them without
// calling a hook per iteration.
export const OptionGroupBlock: FC<{ group: Group }> = ({ group }) => {
  const state = useOptionGroupValue(group, 'radio');
  return (
    <OptionGroup
      items={group.items}
      group={state}
      label={!!group.groupTitle && <Menu.ItemGroupLabel>{group.groupTitle}</Menu.ItemGroupLabel>}
    />
  );
};
